import {LitElement, html} from 'lit';
import {SignalWatcher, signal} from '@lit-labs/preact-signals';
import {clamAdminShellStyles} from './clam-admin-shell.css.js';
import {authService} from '../../auth/auth.service.js';
import {userSignal} from '../../auth/auth.store.js';
import {loadEntityDefinitions, loadToolConfigs, type EntityDefinition, type ToolConfig} from '../../services/entity-definition.service.js';
import '../clam-entity-manager/clam-entity-manager.js';
import '../clam-scanner/clam-scanner.js';
import '../clam-people-loader/clam-people-loader.js';
import '../clam-gafete-loader/clam-gafete-loader.js';
import '../clam-qr-manager/clam-qr-manager.js';
import '../clam-email-template-editor/clam-email-template-editor.js';
import '../clam-admin-home/clam-admin-home.js';
import '../clam-bienvenida/clam-bienvenida.js';
import '../clam-user-manager/clam-user-manager.js';
import '../clam-role-manager/clam-role-manager.js';
import '../clam-cognito-importer/clam-cognito-importer.js';
import {loadPermissions, loadMyProfile, canView, hasTool, canAccessMorphed, hasAnyTool, permissionsLoaded} from '../../services/permission.service.js';
import {iconWarning} from '../../utils/icons.js';

type View = { kind: 'home' } | { kind: 'panel-admin' } | { kind: 'entity'; endpoint: string } | { kind: 'scanner' } | { kind: 'people-loader' } | { kind: 'gafete-loader' } | { kind: 'qr-manager' } | { kind: 'email-template-editor' } | { kind: 'user-manager' } | { kind: 'role-manager' } | { kind: 'cognito-importer' };

const ICON_ENTITY = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/></svg>`;
const ICON_SCANNER = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><path d="M7 12h10"/></svg>`;
const ICON_PEOPLE = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
const ICON_GAFETE = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z"/><circle cx="7" cy="7" r="1.5" fill="currentColor"/></svg>`;
const ICON_QR = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3z"/><path d="M20 14v3"/><path d="M14 20h3"/><path d="M20 20v1"/></svg>`;
const ICON_EMAIL = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`;
const ICON_HOME = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9.5 12 3l9 6.5"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/></svg>`;
const ICON_USERS = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
const ICON_ROLES = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 4 5v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V5l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg>`;
const ICON_MORPHED = html`<svg class="nav-item__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"/></svg>`;

export class ClamAdminShell extends SignalWatcher(LitElement) {
    static styles = clamAdminShellStyles;

    private _view = signal<View>({ kind: 'home' });
    private _drawerOpen = signal(false);
    private _defs: EntityDefinition[] = [];
    private _tools: ToolConfig[] = [];
    private _loaded = false;
    private _loadError = signal('');
    private _bootState = signal<'loading' | 'done' | 'error'>('loading');
    private _prefilledEvento = '';

    connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('keydown', this._onKeydown);
        if (this._loaded) return;
        this._loaded = true;
        void this._boot();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener('keydown', this._onKeydown);
    }

    private _onKeydown = (e: KeyboardEvent): void => {
        if (e.key === 'Escape' && this._drawerOpen.value) this._closeDrawer();
    };

    private _backToAdmin(): void {
        this._view.value = { kind: 'home' };
    }

    private _selectHome(): void {
        this._view.value = { kind: 'home' };
        this._closeDrawer();
    }

    private _selectPanelAdmin(): void {
        this._view.value = { kind: 'panel-admin' };
        this._closeDrawer();
    }

    private async _boot(): Promise<void> {
        this._bootState.value = 'loading';
        try {
            this._defs = await loadEntityDefinitions(true);
            this._tools = await loadToolConfigs(true);
            await loadPermissions(true);
            await loadMyProfile(true);
            if (this._defs.length > 0) {
                // Preferencia admin: si el usuario puede ver el Inicio Admin (dev log), es su pantalla default.
                if (this._view.value.kind === 'home' && canView('clam-admin-home')) {
                    this._view.value = { kind: 'panel-admin' };
                }
                this._bootState.value = 'done';
            } else {
                this._loadError.value = 'No se encontraron entidades CLAM. Verifica que morphed-be esté corriendo.';
                this._bootState.value = 'error';
            }
        } catch (e) {
            console.error('[shell] failed to load definitions:', e);
            this._loadError.value = String(e);
            this._bootState.value = 'error';
        }
        this.requestUpdate();
    }

    private _toggleDrawer(): void {
        this._drawerOpen.value = !this._drawerOpen.value;
        this.requestUpdate();
    }

    private _closeDrawer(): void {
        this._drawerOpen.value = false;
        this.requestUpdate();
    }

    private _selectEntity(endpoint: string): void {
        this._view.value = { kind: 'entity', endpoint };
        this._closeDrawer();
    }

    private _selectScanner(): void {
        this._view.value = { kind: 'scanner' };
        this._closeDrawer();
    }

    private _selectPeopleLoader(): void {
        this._view.value = { kind: 'people-loader' };
        this._closeDrawer();
    }

    private _selectGafeteLoader(): void {
        this._view.value = { kind: 'gafete-loader' };
        this._closeDrawer();
    }

    private _selectQrManager(): void {
        this._view.value = { kind: 'qr-manager' };
        this._closeDrawer();
    }

    private _selectEmailTemplateEditor(): void {
        this._prefilledEvento = '';
        this._view.value = { kind: 'email-template-editor' };
        this._closeDrawer();
    }

    private _selectUserManager(): void {
        this._view.value = { kind: 'user-manager' };
        this._closeDrawer();
    }

    private _selectRoleManager(): void {
        this._view.value = { kind: 'role-manager' };
        this._closeDrawer();
    }

    private _selectCognitoImporter(): void {
        this._view.value = { kind: 'cognito-importer' };
        this._closeDrawer();
    }

    private _openTemplateEditorForEvent(e: CustomEvent<{eventoId: string}>): void {
        this._prefilledEvento = e.detail?.eventoId || '';
        this._view.value = { kind: 'email-template-editor' };
        this._closeDrawer();
    }

    private _onLogout(): void {
        void authService.logout();
    }

    private _entityLabel(d: EntityDefinition): string {
        const tool = this._tools.find((t) => t.entityName === d.entityName);
        return tool?.toolLabel || d.entityName.replace('Clam ', '');
    }

    private _toolFor(d: EntityDefinition): ToolConfig | undefined {
        return this._tools.find((t) => t.entityName === d.entityName);
    }

    private _groupKey(d: EntityDefinition): string {
        const g = this._toolFor(d)?.toolGroup;
        if (g) return g;
        return d.public ? 'catalog' : 'system';
    }

    private _groupLabel(key: string): string {
        switch (key) {
            case 'catalog': return 'Contenido [web]';
            case 'design': return 'Diseño';
            case 'commerce': return 'Comercio';
            case 'config': return 'Configuración';
            case 'system': return 'Plataforma';
            default: return key;
        }
    }

    private get _groupedDefs(): { key: string; label: string; defs: EntityDefinition[] }[] {
        const GROUP_ORDER = ['catalog', 'design', 'commerce', 'config', 'system'];
        const INTERNAL = ['clam-tool-config', 'clam-mailer', 'clam-rol'];
        const sorted = [...this._defs]
            .filter((d) => !d.entityName.includes('Tool Config'))
            .filter((d) => d.entitySchema === 'clam')
            .filter((d) => !INTERNAL.includes(d.entityEndpoint))
            .sort((a, b) => (this._toolFor(a)?.sortOrder ?? 9999) - (this._toolFor(b)?.sortOrder ?? 9999));
        const buckets = new Map<string, EntityDefinition[]>();
        for (const d of sorted) {
            const k = this._groupKey(d);
            if (!buckets.has(k)) buckets.set(k, []);
            buckets.get(k)!.push(d);
        }
        const keys = [...buckets.keys()].sort((a, b) => {
            const ia = GROUP_ORDER.indexOf(a);
            const ib = GROUP_ORDER.indexOf(b);
            return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        });
        return keys.map((k) => ({ key: k, label: this._groupLabel(k), defs: buckets.get(k)! }));
    }

    private get _currentTitle(): string {
        const v = this._view.value;
        if (v.kind === 'home') return 'Inicio CLAM';
        if (v.kind === 'panel-admin') return 'Inicio Admin';
        if (v.kind === 'scanner') return 'Escanear Asistencia';
        if (v.kind === 'people-loader') return 'Importar Asistentes';
        if (v.kind === 'gafete-loader') return 'Asignar Gafetes';
        if (v.kind === 'qr-manager') return 'Invitaciones y Correos';
        if (v.kind === 'email-template-editor') return 'Plantillas de Correo';
        if (v.kind === 'user-manager') return 'Usuarios';
        if (v.kind === 'role-manager') return 'Roles';
        if (v.kind === 'cognito-importer') return 'Importar desde Cognito';
        const def = this._defs.find((d) => d.entityEndpoint === v.endpoint);
        return def ? this._entityLabel(def) : 'Admin';
    }

    render() {
        const email = userSignal.value?.email ?? '';
        const v = this._view.value;

        return html`
            <aside class="sidebar ${this._drawerOpen.value ? 'open' : ''}" id="admin-sidebar">
                <div class="sidebar__header">
                    <div class="sidebar__brand" role="button" tabindex="0" style="cursor:pointer" @click=${() => this._selectHome()} @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._selectHome(); } }}>
                        <div class="sidebar__mark">CL</div>
                        <span class="sidebar__brand-text">CLAM Veracruz</span>
                    </div>
                    <button class="sidebar__close" @click=${() => this._closeDrawer()} aria-label="Cerrar">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                </div>

                <nav class="sidebar__nav">
                    ${this._bootState.value === 'loading'
                        ? html`<div style="padding: var(--clam-space-5, 20px); color: var(--clam-light-gray, #898a8c); font-size: var(--clam-text-sm, 13px);">Cargando entidades...</div>`
                        : this._bootState.value === 'error'
                            ? html`<div style="padding: var(--clam-space-5, 20px); color: var(--clam-danger, #ea4335); font-size: var(--clam-text-xs, 11px); line-height:1.5;">
                                <p style="margin:0 0 8px">${iconWarning()} ${this._loadError.value.substring(0, 120)}</p>
                                <button @click=${() => this._boot()} style="background:rgba(107,187,36,0.2);border:1px solid var(--clam-light-green,#6bbb24);color:var(--clam-white-green,#b6d7a8);padding:6px 12px;border-radius:6px;cursor:pointer;font-size:12px;font-family:inherit;">Reintentar</button>
                              </div>`
                            : html`
                                <div class="nav-item ${v.kind === 'home' ? 'active' : ''}" @click=${() => this._selectHome()}>
                                    ${ICON_HOME}
                                    <span>Inicio CLAM</span>
                                </div>
                                ${canView('clam-admin-home') ? html`
                                <div class="nav-item ${v.kind === 'panel-admin' ? 'active' : ''}" @click=${() => this._selectPanelAdmin()}>
                                    ${ICON_HOME}
                                    <span>Inicio Admin</span>
                                </div>` : ''}
                                ${this._groupedDefs
                                    .map((g) => ({...g, defs: g.defs.filter((d) => hasTool(d.entityEndpoint))}))
                                    .filter((g) => g.defs.length > 0)
                                    .map((g) => html`
                                        <div class="nav-group">${g.label}</div>
                                        ${g.defs.map((d) => this._renderEntityNav(d))}
                                    `)}
                                ${(canView('people-loader') || canView('gafete-loader') || canView('qr-manager') || canView('email-template-editor') || canView('scanner') || canView('role-manager') || canView('user-manager'))
                                    ? html`<div class="nav-group">Herramientas</div>` : ''}
                                ${canView('people-loader') ? html`
                                <div class="nav-item ${v.kind === 'people-loader' ? 'active' : ''}" @click=${() => this._selectPeopleLoader()}>
                                    ${ICON_PEOPLE}
                                    <span>Importar Asistentes</span>
                                </div>` : ''}
                                ${canView('gafete-loader') ? html`
                                <div class="nav-item ${v.kind === 'gafete-loader' ? 'active' : ''}" @click=${() => this._selectGafeteLoader()}>
                                    ${ICON_GAFETE}
                                    <span>Asignar Gafetes</span>
                                </div>` : ''}
                                ${canView('qr-manager') ? html`
                                <div class="nav-item ${v.kind === 'qr-manager' ? 'active' : ''}" @click=${() => this._selectQrManager()}>
                                    ${ICON_QR}
                                    <span>Invitaciones y Correos</span>
                                </div>` : ''}
                                ${canView('email-template-editor') ? html`
                                <div class="nav-item ${v.kind === 'email-template-editor' ? 'active' : ''}" @click=${() => this._selectEmailTemplateEditor()}>
                                    ${ICON_EMAIL}
                                    <span>Plantillas de Correo</span>
                                </div>` : ''}
                                ${canView('scanner') ? html`
                                <div class="nav-item ${v.kind === 'scanner' ? 'active' : ''}" @click=${() => this._selectScanner()}>
                                    ${ICON_SCANNER}
                                    <span>Escanear Asistencia</span>
                                </div>` : ''}
                                ${canView('role-manager') ? html`
                                <div class="nav-item ${v.kind === 'role-manager' ? 'active' : ''}" @click=${() => this._selectRoleManager()}>
                                    ${ICON_ROLES}
                                    <span>Roles</span>
                                </div>` : ''}
                                ${canView('user-manager') ? html`
                                <div class="nav-item ${v.kind === 'user-manager' ? 'active' : ''}" @click=${() => this._selectUserManager()}>
                                    ${ICON_USERS}
                                    <span>Usuarios</span>
                                </div>` : ''}
                                ${canAccessMorphed.value ? html`
                                <div class="nav-group">Morphed</div>
                                <div class="nav-item ${v.kind === 'entity' && v.endpoint === 'morphed-user' ? 'active' : ''}" @click=${() => this._selectEntity('morphed-user')}>
                                    ${ICON_MORPHED}
                                    <span>Usuarios Morphed</span>
                                </div>
                                <div class="nav-item ${v.kind === 'entity' && v.endpoint === 'morphed-organization' ? 'active' : ''}" @click=${() => this._selectEntity('morphed-organization')}>
                                    ${ICON_MORPHED}
                                    <span>Organizaciones</span>
                                </div>
                                <div class="nav-item ${v.kind === 'cognito-importer' ? 'active' : ''}" @click=${() => this._selectCognitoImporter()}>
                                    ${ICON_USERS}
                                    <span>Importar desde Cognito</span>
                                </div>` : ''}
                            `}
                </nav>

                <div class="sidebar__footer">
                    <div class="sidebar__user">
                        <span class="sidebar__user-email">${email}</span>
                    </div>
                    <button class="sidebar__logout" @click=${() => this._onLogout()}>Cerrar Sesión</button>
                </div>
            </aside>

            <div class="sidebar-overlay ${this._drawerOpen.value ? 'show' : ''}" @click=${() => this._closeDrawer()}></div>

            <div class="main">
                <header class="topbar">
                    <button class="hamburger" @click=${() => this._toggleDrawer()} aria-label="Menú" aria-expanded=${this._drawerOpen.value} aria-controls="admin-sidebar">
                        <span></span><span></span><span></span>
                    </button>
                    <span class="topbar__title">${this._currentTitle}</span>
                </header>
                <div class="content">
                    ${v.kind === 'home'
                        ? html`<clam-bienvenida></clam-bienvenida>`
                        : v.kind === 'panel-admin'
                            ? (!permissionsLoaded.value
                                ? html`<div style="padding:60px 24px;text-align:center;color:var(--clam-light-gray,#898a8c)"><div style="display:inline-block;width:22px;height:22px;border:3px solid var(--clam-border,#e2e8f0);border-top-color:var(--clam-primary,#5a9d1e);border-radius:50%;animation:clspin .7s linear infinite;vertical-align:middle;margin-right:10px"></div>Cargando permisos…</div>`
                                : !hasAnyTool.value && !canAccessMorphed.value
                                    ? html`<div style="padding:60px 24px;text-align:center;color:var(--clam-light-gray,#898a8c)"><h2 style="margin:0 0 8px;color:var(--clam-text,#333)">Sin acceso a herramientas</h2><p style="margin:0">Tu cuenta no tiene herramientas asignadas todavía. Contacta al administrador.</p></div>`
                                    : html`<clam-admin-home .entityCount=${this._defs.length} .toolCount=${this._tools.length}></clam-admin-home>`)
                        : v.kind === 'scanner'
                            ? html`<clam-scanner @back-to-admin=${() => this._backToAdmin()}></clam-scanner>`
                            : v.kind === 'people-loader'
                                ? html`<clam-people-loader></clam-people-loader>`
                                : v.kind === 'gafete-loader'
                                    ? html`<clam-gafete-loader></clam-gafete-loader>`
                                    : v.kind === 'qr-manager'
                                    ? html`<clam-qr-manager @open-template-editor=${(e: CustomEvent<{eventoId: string}>) => this._openTemplateEditorForEvent(e)}></clam-qr-manager>`
                                    : v.kind === 'email-template-editor'
                                        ? html`<clam-email-template-editor .prefilledEvento=${this._prefilledEvento}></clam-email-template-editor>`
                                        : v.kind === 'user-manager'
                                            ? html`<clam-user-manager></clam-user-manager>`
                                            : v.kind === 'role-manager'
                                                ? html`<clam-role-manager></clam-role-manager>`
                                                : v.kind === 'cognito-importer'
                                                    ? html`<clam-cognito-importer></clam-cognito-importer>`
                                                    : (v.endpoint
                                            ? html`<clam-entity-manager .entityEndpoint=${v.endpoint}></clam-entity-manager>`
                                            : html`<div style="padding:40px;text-align:center;color:var(--clam-light-gray,#898a8c)">Selecciona una entidad del menú</div>`)}
                </div>
            </div>
        `;
    }

    private _renderEntityNav(d: EntityDefinition) {
        const v = this._view.value;
        const isActive = v.kind === 'entity' && v.endpoint === d.entityEndpoint;
        return html`
            <div class="nav-item ${isActive ? 'active' : ''}" @click=${() => this._selectEntity(d.entityEndpoint)}>
                ${ICON_ENTITY}
                <span>${this._entityLabel(d)}</span>
                ${d.public ? html`<span class="badge badge-web">web</span>` : html`<span class="badge badge-priv">priv</span>`}
            </div>
        `;
    }
}

customElements.define('clam-admin-shell', ClamAdminShell);

declare global {
    interface HTMLElementTagNameMap {
        'clam-admin-shell': ClamAdminShell;
    }
}
